import React from "react";
import { useTrail, animated } from "@react-spring/web";

import { Group } from "./Group";
import { Header } from "./Header";
import { InfoSymbol } from "./InfoSymbol";
import { Footer } from "./Footer";

import { chartColors } from "../../../config/colors";

interface Props {
  id: string;
  text: string;
  title?: string;
  description?: string;
  source?: string;
  sourceLink?: string;
  infoLink?: string;
  color?: string;
  width?: number;
  height?: number;
  isAnimated?: boolean;
}

export const NumberChart: React.FC<Props> = ({
  id,
  text,
  title,
  description,
  source,
  sourceLink,
  infoLink,
  color = chartColors.linePrimary,
  width = 210,
  height = 160,
  isAnimated = true,
}) => {
  const margin = { top: 60, right: 0, bottom: 30, left: 0 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const chars = text.split("");

  const textAnimations = useTrail(chars.length, {
    config: {
      duration: isAnimated ? 1000 / chars.length : 0,
    },
    dy: isAnimated ? "1em" : "5em",
    opacity: isAnimated ? 1 : 0,
  });

  return (
    <>
      {title && <Header title={title} description={description} />}
      {infoLink && (
        <InfoSymbol
          link={infoLink}
          transform={`translate(${innerWidth - 15}, ${-5})`}
        />
      )}
      {source && (
        <Footer
          text={`Quelle: ${source}`}
          link={sourceLink}
          alignRight={true}
          transform={`translate(${width}, ${height})`}
        />
      )}
      <Group transform={`translate(${margin.left}, ${margin.top})`}>
        <defs>
          <clipPath id={`clip-${id}`}>
            <rect width={innerWidth} height={innerHeight + 10} />
          </clipPath>
        </defs>
        <text
          clipPath={`url(#clip-${id})`}
          fontFamily="TheMixExtraBold, sans-serif"
          fontWeight="700"
          fontSize="58"
          fill={color}
          dy="1em"
        >
          {textAnimations.map((textAnimation, index) => (
            <animated.tspan key={index} {...textAnimation} y="0">
              {chars[index]}
            </animated.tspan>
          ))}
        </text>
      </Group>
    </>
  );
};
